import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, PlayCircle } from 'lucide-react';

export const ONBOARDING_VIDEO_SEEN_KEY = 'la360_onboarding_video_v1';
// Google Drive preview embed (file must be shared "anyone with the link").
const VIDEO_EMBED_URL = 'https://drive.google.com/file/d/1OYGNFKF_XsEuIa5I8TQb7ql_OMqCdgkW/preview';

function markSeen() {
  try { localStorage.setItem(ONBOARDING_VIDEO_SEEN_KEY, '1'); } catch (_e) { /* ignore */ }
}

/**
 * Onboarding welcome video. Auto-opens once for new clients on first login, and
 * stays permanently accessible via the "Watch intro" button (next to Take a tour).
 */
export default function OnboardingVideo({ user, enabled = true, onClose }) {
  const [open, setOpen] = useState(false);
  const openedRef = useRef(false);

  useEffect(() => {
    if (!enabled || user?.role !== 'client' || openedRef.current) return;
    let seen = false;
    try { seen = localStorage.getItem(ONBOARDING_VIDEO_SEEN_KEY) === '1'; } catch (_e) { seen = false; }
    if (seen) return;
    openedRef.current = true;
    setOpen(true);
  }, [user, enabled]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!enabled) return null;

  const handleClose = () => { markSeen(); setOpen(false); if (onClose) onClose(); };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-40 right-6 z-40 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-violet-600 text-white text-sm font-semibold shadow-lg shadow-violet-600/30 hover:bg-violet-500 transition-all"
        data-testid="watch-intro-btn"
      >
        <PlayCircle className="w-4 h-4" />
        Watch intro
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[1000] flex items-start sm:items-center justify-center p-4 overflow-y-auto" role="dialog" aria-modal="true" data-testid="onboarding-video-modal">
          <button type="button" aria-label="Close" className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClose} />
          <div className="relative w-full max-w-3xl my-auto">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-white text-lg font-bold" style={{ fontFamily: 'Outfit' }}>Welcome to Law Agency 360 👋</div>
                <div className="text-slate-300 text-sm">Watch this quick intro to get the most out of your dashboard.</div>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition flex-shrink-0"
                aria-label="Close"
                data-testid="onboarding-video-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="rounded-2xl overflow-hidden bg-black shadow-2xl ring-1 ring-white/10">
              <iframe
                src={VIDEO_EMBED_URL}
                title="Law Agency 360 onboarding video"
                className="w-full aspect-video block"
                allow="autoplay; fullscreen"
                allowFullScreen
              />
            </div>
            <div className="text-right mt-3">
              <button
                type="button"
                onClick={handleClose}
                className="px-5 py-2.5 rounded-xl bg-white text-slate-900 text-sm font-semibold hover:bg-slate-100 transition"
                data-testid="onboarding-video-done"
              >
                Got it
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
